
import Vue from 'vue';
import Component from 'vue-class-component';
import CustomTable from '@/core/components/shared/CustomTable.vue';
import { Inject } from 'inversify-props';
import { TYPES } from '@/core/config/Types';
import { StockFindAll } from '@/wms/application/stock/search/FindallStock';
import { Store } from '@/wms/domain/layout/store/Store';
import { References } from '@/wms/domain/catalogs/references/References';
import { ReferenceFindAll } from '@/wms/application/catalogs/references/search/ReferenceFindAll';
import { FindAllStore } from '@/wms/application/layout/store/search/FindAllStore';
import { FindAllMovements } from '@/wms/application/stock/search/FindAllMovements';

@Component({
  name: 'InventoryDashboard',
  components: { CustomTable }
})
export default class InventoryDashboard extends Vue {
  @Inject(TYPES.FINDALL_STOCK) readonly findAllStock!: StockFindAll;
  @Inject(TYPES.FINDALL_REFERENCE) readonly findAllReference!: ReferenceFindAll;
  @Inject(TYPES.FIND_ALL_STORE) readonly findAllStore!: FindAllStore;
  @Inject(TYPES.FIND_STOCK_MOVES) readonly findStockMoves!: FindAllMovements;
  //DATA
  isLoading = false;
  stockItems: any[] = [];
  movimientoHistorico: any[] = [];
  //TABS CONTROL
  tabControl = 0;
  //SELECTEDS
  selectedFilterOptions: any = null;
  //OPTIONS
  references: References[] = [];
  stores: Store[] = [];
  //GETTERS
  get fields() {
    return [
      {
        key: 'code',
        label: `${this.$t('general.SKU')}`,
        sortable: true
      },
      {
        key: 'description',
        label: `${this.$t('general.description')}`,
        sortable: true
      },
      {
        key: 'ano',
        label: `${this.$t('general.year')}`,
        sortable: true
      },
      {
        key: 'mes',
        label: `${this.$t('general.month')}`,
        sortable: true
      },
      {
        key: 'store_name',
        label: `${this.$t('general.store')}`,
        sortable: true
      },
      {
        key: 'can_ini',
        label: `${this.$t('general.initialStock')}`,
        sortable: true
      },
      {
        key: 'can_ent',
        label: `${this.$t('general.enteredQuantity')}`,
        sortable: true
      },
      {
        key: 'can_sal',
        label: `${this.$t('general.outQuantity')}`,
        sortable: true
      },
      {
        key: 'stock',
        label: `${this.$t('general.finalStock')}`,
        sortable: true
      }
    ];
  }

  get fieldsMovimientoHistorico() {
    return [
      {
        key: 'storeName',
        label: `${this.$t('general.store')}`,
        sortable: true
      },
      {
        key: 'date',
        label: `${this.$t('general.date')}`,
        sortable: true,
        formatter: (value: any, key: number, item: any) => {
          return `${item.dateDoc}, ${item.timeDoc}`;
        }
      },
      {
        key: 'switchCode',
        label: `${this.$t('general.nature')}`,
        sortable: true
      },
      {
        key: 'number',
        label: `${this.$t('general.number')}`,
        sortable: true
      },
      {
        key: 'entry',
        label: `${this.$t('general.in')}`,
        sortable: true
      },
      {
        key: 'invoice',
        label: `${this.$t('general.out')}`,
        sortable: true
      },
      {
        key: 'stockBalance',
        label: `${this.$t('general.stockBalance')}`,
        sortable: true
      },
      {
        key: 'stockValue',
        label: `${this.$t('general.stockValue')}`,
        sortable: true
      },
      {
        key: 'unitCost',
        label: `${this.$t('general.unitCost')}`,
        sortable: true
      },
      {
        key: 'totalCost',
        label: `${this.$t('general.totalCost')}`,
        sortable: true
      },
      {
        key: 'unitPrice',
        label: `${this.$t('general.unitPrice')}`,
        sortable: true
      },
      {
        key: 'discount',
        label: `${this.$t('general.discount')}`,
        sortable: true
      },
      {
        key: 'totalPrice',
        label: `${this.$t('general.totalPrice')}`,
        sortable: true
      },
      {
        key: 'marginValue',
        label: `${this.$t('general.marginValue')}`,
        sortable: true
      },
      {
        key: 'store',
        label: `%`,
        sortable: true
      }
    ];
  }

  get allowFilters() {
    return (
      this.filterSearch.year != '' &&
      this.filterSearch.from != '' &&
      this.filterSearch.to != '' &&
      this.filterSearch.reference != null
    );
  }

  //TABLE FILTER
  filterSearch: {
    year: string;
    from: string;
    to: string;
    store: Store | null;
    reference: References | null;
  } = {
    year: '',
    from: '',
    to: '',
    store: null,
    reference: null
  };
  //MOUNT COMPONENT
  async mounted() {
    await this.findAll();
    await this.getReferences();
    await this.getStores();
  }
  //GETTERS

  get filterOptions() {
    return [
      {
        id: 'store',
        label: `${this.$t('general.store')}`
      },
      {
        id: 'ref',
        label: `${this.$t('general.reference')}`
      },
      {
        id: 'date',
        label: `${this.$t('general.date')}`
      }
    ];
  }

  //FILTER FUNCTIONS
  filterTable(
    row: any,
    filter: {
      year: string;
      from: string;
      to: string;
      store: Store | null;
      reference: References | null;
    }
  ) {
    if (filter.year == '' || filter.from == '' || filter.to == '' || filter.store == null || filter.reference == null)
      return true;

    if (
      filter.year == row.ano &&
      row.mes >= filter.from &&
      row.mes <= filter.to &&
      filter.store?.storeId == row.bodega &&
      filter.reference?.code == row.code
    )
      return true;

    return false;
  }

  resetFilter() {
    this.filterSearch = {
      year: '',
      from: '',
      to: '',
      store: null,
      reference: null
    };
  }

  //METHODS
  async findAll() {
    this.isLoading = true;
    try {
      const res = await this.findAllStock.execute();
      this.stockItems = res;
    } catch (error) {
      throw new Error(`${error}`);
    }
    this.isLoading = false;
  }

  async getReferences() {
    try {
      this.isLoading = true;
      const res = await this.findAllReference.execute();
      this.references = res.length > 0 ? res : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }

  async getStores() {
    try {
      this.isLoading = true;
      const res = await this.findAllStore.execute();
      this.stores = res.length > 0 ? res : [];
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }
  //HANDDLERS
  handdleMonthsChange(input: string) {
    if (parseInt(input) < 1 || parseInt(input) > 12) {
      this.filterSearch.from = '1';
      this.filterSearch.to = '12';
    }
  }

  verifyMonths() {
    if (this.filterSearch.from > this.filterSearch.to && this.filterSearch.from != '' && this.filterSearch.to != '') {
      const aux = this.filterSearch.from;
      this.filterSearch.from = this.filterSearch.to;
      this.filterSearch.to = aux;
    }
  }
  //CUSTOM LABELS
  codeDescriptionReference(reference: References) {
    return `${reference.code} - ${reference.filterBrand}`;
  }
  codeNameStore(store: Store) {
    return `${store.storeId} - ${store.storeName}`;
  }

  async searchSKUMoves() {
    try {
      this.isLoading = true;
      const res = await this.findStockMoves.execute({
        fMonth: parseInt(this.filterSearch.to) < 10 ? `0${this.filterSearch.to}` : this.filterSearch.to,
        iMonth: parseInt(this.filterSearch.from) < 10 ? `0${this.filterSearch.from}` : this.filterSearch.from,
        sku: this.filterSearch.reference?.code,
        store: this.filterSearch.store != null ? this.filterSearch.store.storeId : 0,
        year: this.filterSearch.year
      });
      this.movimientoHistorico = res.length > 0 ? res : [];
      this.tabControl = 2;
      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      throw new Error(`${error}`);
    }
  }
}
